import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  UserCircleIcon,
  KeyIcon,
  BellIcon,
  PaintBrushIcon,
  TagIcon,
  CpuChipIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { useAuthStore, useThemeStore, type Theme } from '@/store';
import { Button, Card, CardHeader, Input, Select } from '@/components/common';
import { getFieldErrors } from '@/services/api';
import { useChangePasswordSettings, usePreferences, useUpdatePreferences } from '@/hooks/useSettings';
// POSP #2: the Profile tab's two cards are no longer written here. They are one
// component now, shared with the agent's own profile page, so the two screens
// cannot drift and an agent is not shown the same facts twice.
import { AccountProfileSection } from './AccountProfileSection';
// M-01: a customer's Profile tab is their whole profile -- details, KYC,
// policies, advisors and password -- rather than a link out to another page.
import { CustomerProfileSection } from './CustomerProfileSection';
// P2 / decision 16: a POSP's Profile tab is their WHOLE agent profile -- the
// account details above plus status, certifications, bank, broker mapping and
// documents -- rather than a link out to a second settings page.
import { PospProfileSection } from './PospProfileSection';
import { NotificationChannelsCard } from './NotificationChannelsCard';
import { PrivacyPreferencesCard } from './PrivacyPreferencesCard';
// Decision 18: TOTP two-factor, on the Password tab.
import { TwoFactorPanel } from './TwoFactorPanel';
// Messaging Plan 4: personal AI assistant (MCP) keys.
import { AssistantKeysPanel } from './AssistantKeysPanel';
import { ASSISTANT_KEY_ROLES } from './settingsAccess';

const SAME_PASSWORD_MESSAGE = 'New password must be different from your current password';

const passwordSchema = z.object({
  current_password: z.string().min(1, 'Current password is required'),
  new_password: z.string().min(8, 'Password must be at least 8 characters'),
  confirm_password: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.new_password === data.confirm_password, {
  message: "Passwords don't match",
  path: ['confirm_password'],
}).refine((data) => data.new_password !== data.current_password, {
  // Mirrors the server (#416) so the message shows before a round trip.
  message: SAME_PASSWORD_MESSAGE,
  path: ['new_password'],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

/** Roles that administer the platform/broker configuration screens linked from
 *  this page (board #262). */
const ADMIN_SETTINGS_ROLES = ['super_admin', 'country_admin', 'broker_admin'];

const THEME_VALUES: Theme[] = ['light', 'dark', 'system'];
const isTheme = (value: unknown): value is Theme =>
  typeof value === 'string' && (THEME_VALUES as string[]).includes(value);

const themeOptions = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

export function SettingsPage() {
  const role = useAuthStore((state) => state.user?.role);

  // M-01: a customer's Profile tab is `CustomerProfileSection` (it carries
  // Reset Password itself), so they get no separate Password tab. It used to
  // be a "My Account" link out to /settings/my-profile, which now redirects
  // here.
  const isCustomer = role === 'customer';
  // P2 / decision 16: a POSP's Profile tab is `PospProfileSection`, which opens
  // with the very same `AccountProfileSection` every other role gets. Password
  // stays its own tab, directly below Profile.
  const isPosp = role === 'posp' || role === 'super_posp';
  const holdsAssistantKeys = role !== undefined && ASSISTANT_KEY_ROLES.includes(role);
  const allTabs = [
    { id: 'profile', label: 'Profile', icon: UserCircleIcon },
    { id: 'password', label: 'Password', icon: KeyIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'appearance', label: 'Appearance', icon: PaintBrushIcon },
    { id: 'assistants', label: 'AI assistants', icon: CpuChipIcon },
  ] as const;
  const tabs = allTabs.filter(
    (tab) => (tab.id !== 'password' || !isCustomer) && (tab.id !== 'assistants' || holdsAssistantKeys)
  );

  // Profile first for every role.
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'notifications' | 'appearance' | 'assistants'>('profile');

  // Adjust-state-during-render (React's own pattern for this): if role
  // resolves to customer after mount (the auth store hydrating from storage a
  // tick late), fall back off the Password tab that has just disappeared
  // instead of rendering blank content for it.
  if (isCustomer && activeTab === 'password') {
    setActiveTab('profile');
  }

  const { data: preferences, isLoading: preferencesLoading } = usePreferences();
  const changePasswordMutation = useChangePasswordSettings();
  const updatePreferencesMutation = useUpdatePreferences();

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const onPasswordSubmit = (data: PasswordFormData) => {
    changePasswordMutation.mutate(data, {
      onSuccess: () => {
        passwordForm.reset();
      },
      onError: (error) => {
        // Show the server's per-field message under the field it belongs to.
        const fieldErrors = getFieldErrors(error);
        if (fieldErrors.new_password) {
          passwordForm.setError('new_password', { type: 'server', message: fieldErrors.new_password });
        }
      },
    });
  };

  const handlePreferenceChange = (key: string, value: boolean | string) => {
    updatePreferencesMutation.mutate({ [key]: value });
    // The theme lives in two places: this preference row (so it follows the
    // user to another device) and the local theme store (which is what actually
    // paints). Writing only the row left the dropdown disagreeing with the
    // visible theme until the next page load, so apply it here too.
    if (key === 'theme' && isTheme(value)) {
      useThemeStore.getState().setTheme(value);
    }
  };

  // Board #262: the Support Categories screen is administration, not a personal
  // preference, so it is its own route rather than a fifth tab here. This is
  // the only way into it -- a page with no link is a page nobody finds.
  const adminLinks = ADMIN_SETTINGS_ROLES.includes(role ?? '')
    ? [
        {
          to: '/settings/support-categories',
          label: 'Support Categories',
          icon: TagIcon,
          description: 'What a support ticket can be filed under',
        },
      ]
    : [];

  // Decision 16 removed the last portal link from this list: "My Account > My
  // Agent Profile" pointed at a second settings screen, and that screen's body
  // is now the POSP's Profile tab. A customer lost theirs to M-01 for the same
  // reason. So the group that remains is administration only.
  const sideLinks = adminLinks;

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Manage your account settings and preferences"
        breadcrumbs={[{ label: 'Settings' }]}
      />

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:w-64 flex-shrink-0">
          <Card padding="none">
            <nav className="p-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </Card>

          {sideLinks.length > 0 && (
            <Card padding="none" className="mt-4">
              <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Administration
              </p>
              <nav className="p-2 pt-1">
                {sideLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white transition-colors"
                  >
                    <link.icon className="w-5 h-5" />
                    <span className="flex-1 text-left">{link.label}</span>
                    <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                  </Link>
                ))}
              </nav>
            </Card>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Profile Tab. Three shapes of the same tab:
              M-01: a customer's is their Customer profile (KYC, policies,
              advisors, password) instead.
              Decision 16: a POSP's is their whole agent profile, which OPENS
              with the account details below -- not a second copy of them.
              Everyone else gets the account details alone. */}
          {!isCustomer && !isPosp && activeTab === 'profile' && <AccountProfileSection />}
          {isPosp && activeTab === 'profile' && <PospProfileSection />}
          {isCustomer && activeTab === 'profile' && <CustomerProfileSection />}

          {/* Password Tab — C1: only for non-customer roles */}
          {!isCustomer && activeTab === 'password' && (
            <Card>
              <CardHeader
                title="Change Password"
                subtitle="Update your account password"
              />
              <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}>
                <div className="space-y-6 max-w-md">
                  <Input
                    label="Current Password"
                    type="password"
                    error={passwordForm.formState.errors.current_password?.message}
                    required
                    {...passwordForm.register('current_password')}
                  />
                  <Input
                    label="New Password"
                    type="password"
                    error={passwordForm.formState.errors.new_password?.message}
                    required
                    {...passwordForm.register('new_password')}
                  />
                  <Input
                    label="Confirm New Password"
                    type="password"
                    error={passwordForm.formState.errors.confirm_password?.message}
                    required
                    {...passwordForm.register('confirm_password')}
                  />
                </div>
                <div className="mt-6 flex justify-end">
                  <Button
                    type="submit"
                    isLoading={changePasswordMutation.isPending}
                  >
                    Change Password
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* 9b: the second factor sits with the password, because it is the
              other half of "how you prove it is you". Customers have no
              Password tab at all, so they see neither. */}
          {!isCustomer && activeTab === 'password' && <TwoFactorPanel />}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <Card>
              <CardHeader
                title="Notification Preferences"
                subtitle="Choose how you want to be notified"
              />
              {preferencesLoading ? (
                <div className="animate-pulse space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded" />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Email Notifications</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications via email</p>
                    </div>
                    <button
                      onClick={() => handlePreferenceChange('email_notifications', !preferences?.email_notifications)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        preferences?.email_notifications ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          preferences?.email_notifications ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  <div className="flex items-center justify-between py-3 border-t border-gray-100 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">SMS Notifications</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications via SMS</p>
                    </div>
                    <button
                      onClick={() => handlePreferenceChange('sms_notifications', !preferences?.sms_notifications)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        preferences?.sms_notifications ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          preferences?.sms_notifications ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  <div className="flex items-center justify-between py-3 border-t border-gray-100 dark:border-gray-700">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Push Notifications</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Receive push notifications</p>
                    </div>
                    <button
                      onClick={() => handlePreferenceChange('push_notifications', !preferences?.push_notifications)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        preferences?.push_notifications ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          preferences?.push_notifications ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* #394: customers choose channels per category. */}
          {isCustomer && activeTab === 'notifications' && <NotificationChannelsCard />}
          {/* #347 phase 1: marketing opt-outs and the consent history. */}
          {isCustomer && activeTab === 'notifications' && <PrivacyPreferencesCard />}

          {activeTab === 'assistants' && holdsAssistantKeys && <AssistantKeysPanel />}
          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <Card>
              <CardHeader
                title="Appearance"
                subtitle="Customize the look and feel"
              />
              {preferencesLoading ? (
                <div className="animate-pulse space-y-4">
                  <div className="h-12 bg-gray-100 rounded" />
                </div>
              ) : (
                <div className="max-w-md">
                  <Select
                    label="Theme"
                    options={themeOptions}
                    value={preferences?.theme || 'system'}
                    onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Choose your preferred color scheme.
                  </p>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
